import { Link, useNavigate } from "@tanstack/react-router";
import ThrottledButton from "@core/components/throttledButton";

import {
  Form,
  FormField,
  FormPrefixedInput,
  FormInput,
  Button,
  FormOTPInput,
  cn,
} from "web-design-system";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useDistributorAuthActions } from "creso-core/hooks";
import { useState } from "react";
import { Loader2Icon } from "lucide-react";
import { APIError } from "web-core/utils";
import { arnSchema } from "creso-core/zod";

const validationSchema = z
  .object({
    arn_code: arnSchema,
    password: z.string(),
    otp: z.string(),
    withPassword: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.withPassword) {
        return data.password.length > 0;
      }
      return data.otp.length === 6;
    },
    (data) => ({
      message: data.withPassword ? "Password is required" : "OTP is required",
      path: data.withPassword ? ["password"] : ["otp"],
    })
  );

const USERNAME_ERROR_CODES = [
  "USERNAME",
  "ARN",
  "USER_NOT_FOUND",
  "DISTRIBUTOR_NOT_FOUND",
  "EMPLOYEE_NOT_FOUND",
] as const;

const UNKNOWN_ERROR_TEXT = "unknown error";

function normalizeLoginIdentifier(value?: string) {
  return (value ?? "").trim().replace(/^ARN-/i, "").toLowerCase();
}

function getDetailMessage(
  details: Record<string, unknown> | undefined,
  key: string
) {
  const value = details?.[key];
  if (typeof value === "string" && value.trim().length > 0) {
    return value.trim();
  }
  if (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === "string"
  ) {
    const first = (value[0] as string).trim();
    return first.length > 0 ? first : undefined;
  }
  return undefined;
}

function getUsernameErrorMessage(
  details: Record<string, unknown> | undefined,
  enteredArn?: string
) {
  const arnDetail = getDetailMessage(details, "arn_code");
  if (
    arnDetail &&
    arnDetail.toLowerCase() !== (enteredArn ?? "").toLowerCase()
  ) {
    return arnDetail;
  }

  const usernameDetail = getDetailMessage(details, "username");
  if (
    usernameDetail &&
    normalizeLoginIdentifier(usernameDetail) !==
      normalizeLoginIdentifier(enteredArn)
  ) {
    return usernameDetail;
  }

  return undefined;
}

function getCredentialErrorMessage(
  details: Record<string, unknown> | undefined
) {
  return (
    getDetailMessage(details, "password_or_otp") ||
    getDetailMessage(details, "password") ||
    getDetailMessage(details, "otp") ||
    getDetailMessage(details, "non_field_errors")
  );
}

function getCredentialFallbackMessage(field: "password" | "otp") {
  return field === "password"
    ? "Incorrect username or password"
    : "Incorrect username or OTP";
}

export default function DistributorLogin() {
  const { login, requestOtp, isLoginPending, isRequestOtpPending } =
    useDistributorAuthActions();

  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      arn_code: "",
      password: "",
      otp: "",
      withPassword: true,
    },
  });

  const setAuthFieldError = (
    error: unknown,
    options: { defaultField: "password" | "otp" }
  ) => {
    const defaultMessage = "Something went wrong, please try again.";

    if (!(error instanceof APIError)) {
      form.setError(options.defaultField, { message: defaultMessage });
      return;
    }

    const code = (error.data?.code ?? "").toUpperCase();
    const apiMessage = error.data?.message || error.message || defaultMessage;
    const details =
      error.data?.details && typeof error.data.details === "object"
        ? (error.data.details as Record<string, unknown>)
        : undefined;
    const enteredArn = form.getValues("arn_code")?.trim();
    const usernameMessage = getUsernameErrorMessage(details, enteredArn);

    if (USERNAME_ERROR_CODES.some((errorCode) => code.includes(errorCode))) {
      form.setError("arn_code", { message: usernameMessage || apiMessage });
      return;
    }

    const credentialMessage = getCredentialErrorMessage(details);
    if (credentialMessage) {
      form.setError(options.defaultField, { message: credentialMessage });
      return;
    }

    const message = apiMessage.toLowerCase().includes(UNKNOWN_ERROR_TEXT)
      ? getCredentialFallbackMessage(options.defaultField)
      : apiMessage;

    if (code.includes("PASSWORD")) {
      form.setError("password", { message });
      return;
    }

    if (code.includes("OTP")) {
      form.setError("otp", { message });
      return;
    }

    form.setError(options.defaultField, { message });
  };

  const onSubmit = async (values: z.infer<typeof validationSchema>) => {
    const withPassword = form.watch("withPassword");
    const passwordOrOtp = withPassword ? values.password : values.otp;
    const defaultField = withPassword ? "password" : "otp";

    try {
      form.clearErrors(["arn_code", "password", "otp"]);
      await login(values.arn_code, passwordOrOtp);
      navigate({
        to: "/",
      });
    } catch (error) {
      setAuthFieldError(error, { defaultField });
    }
  };

  const [otpSent, setOtpSent] = useState(false);

  const handleSwitchLogin = async () => {
    const isValid = await form.trigger("arn_code");
    if (!isValid) {
      const arn = form.getValues("arn_code");
      form.setError("arn_code", {
        message: arn ? "Username is invalid" : "Username is required",
      });
      return;
    }
    if (!form.watch("withPassword")) {
      form.setValue("withPassword", !form.watch("withPassword"));
      return;
    }
    requestOtp(form.getValues("arn_code"), {
      onSuccess: () => {
        form.clearErrors("arn_code");
        form.setValue("withPassword", false);
        setOtpSent(true);
      },
      onError: (error) => {
        setAuthFieldError(error, { defaultField: "otp" });
      },
    });
  };

  const sendOtp = () => {
    requestOtp(form.getValues("arn_code"), {
      onSuccess: () => {
        form.clearErrors("arn_code");
        setOtpSent(true);
      },
      onError: (error) => {
        setAuthFieldError(error, { defaultField: "otp" });
      },
    });
  };

  return (
    <div className="flex flex-col w-full h-full gap-6">
      <span className="text-2xl font-semibold">Login to your account</span>
      <Form {...form}>
        <form
          className="flex flex-1 flex-col h-full gap-14"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-6 py-8">
            <FormField
              control={form.control}
              name="arn_code"
              render={({ field }) => (
                <FormPrefixedInput
                  {...field}
                  onChange={(value) => {
                    const sanitizedValue = String(value).replace(
                      /[^a-zA-Z0-9._-]/g,
                      ""
                    );
                    field.onChange(sanitizedValue);
                  }}
                  label="Username"
                  type="text"
                  autoComplete="username"
                  prefix=""
                  className="gap-2"
                >
                  ARN
                </FormPrefixedInput>
              )}
            />
            <FormField
              className={cn(!form.watch("withPassword") && "hidden")}
              control={form.control}
              name="password"
              render={({ field }) => (
                <div className="flex flex-col gap-4">
                  <FormInput
                    {...field}
                    label="Password"
                    type="password"
                    autoComplete="current-password"
                    withEye
                    className="gap-2"
                  />
                  <Link
                    className="font-medium text-xs underline w-fit"
                    to="/reset-password"
                    search={{ redirect: "/login/distributor" }}
                  >
                    Forgot password?
                  </Link>
                </div>
              )}
            />
            <FormField
              name="otp"
              className={cn(form.watch("withPassword") && "hidden")}
              render={({ field }) => (
                <div className="flex flex-col gap-1 relative">
                  <FormOTPInput
                    description={otpSent ? "OTP sent!" : undefined}
                    {...field}
                    maxLength={6}
                    label="OTP"
                  />
                  {otpSent && (
                    <ThrottledButton
                      throttle={5}
                      className={cn(
                        form.watch("withPassword") && "hidden",
                        "px-0",
                        "absolute top-0 right-0 h-auto"
                      )}
                      variant="link"
                      size="sm"
                      onClick={sendOtp}
                    >
                      Resend OTP
                    </ThrottledButton>
                  )}
                </div>
              )}
            />
          </div>

          <div className="flex flex-col gap-8 items-center">
            <div className="flex flex-col gap-4 w-full">
              <Button
                size="lg"
                className="w-full"
                type="submit"
                disabled={isLoginPending}
              >
                {isLoginPending && <Loader2Icon className="animate-spin" />}
                Login
              </Button>
              <Button
                size="lg"
                variant="outline"
                disabled={isRequestOtpPending}
                onClick={handleSwitchLogin}
                className="w-full"
                type="button"
              >
                {isRequestOtpPending && (
                  <Loader2Icon className="animate-spin" />
                )}
                {form.watch("withPassword")
                  ? "Login with OTP"
                  : "Login with password"}
              </Button>
            </div>
            <span className="text-center font-medium text-sm">
              New to Creso?{" "}
              <Link className="underline" to="/signup/distributor">
                Signup
              </Link>
            </span>
          </div>

          <div className="text-center text-muted-foreground text-sm flex flex-col gap-0.5">
            <span>Mutual Fund investments are subject to market risks.</span>
            <span>Read all scheme related documents carefully.</span>
          </div>
        </form>
      </Form>
    </div>
  );
}
